import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronRight, Circle, LoaderCircle, X } from 'lucide-react'
import type { DirectorActivity, DirectorTurnPresentation } from '../../generated/directorChatTypes'
import { createNadDebug } from '../../utils/debugLog'

const activityLog = createNadDebug('dw:director-chat-presentation:activity')

/** Compact activity with the corresponding Director text and tool payloads on demand. */
export function DirectorTurnActivity({ presentation }: { presentation: DirectorTurnPresentation }) {
  const { t } = useTranslation('shared')
  const [expanded, setExpanded] = useState(false)
  const activities = presentation.activities ?? []
  // Historical hidden turns have no saved details to recover.
  if (presentation.mode === 'hidden' && activities.length === 0) return null
  const running = presentation.status === 'running'
  const active = [...activities].reverse().find(activity => activity.status === 'running')
  const label = (activity: DirectorActivity) => activity.kind === 'skill' && activity.skill_name
    ? t('directorActivity.usingSkill', { name: activity.skill_name })
    : t(`directorActivity.actions.${activity.action}`)
  // A turn's terminal status records how it ended, not whether its question
  // is still unanswered. The question card owns that live affordance.
  const title = running && active ? label(active) : t(`directorActivity.states.${presentation.status === 'awaiting_user' ? 'completed' : presentation.status}`)
  const TurnIcon = running ? LoaderCircle : presentation.status === 'failed' ? X : presentation.status === 'cancelled' ? Circle : Check

  return (
    <div data-testid="director-turn-activity" data-status={presentation.status} className="text-sm text-muted-foreground">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded(!expanded)
          activityLog({ event: 'toggle', turnId: presentation.turn_id, expanded: !expanded, activityCount: activities.length })
        }}
        className="flex max-w-full items-center gap-2 rounded-lg border border-transparent px-2 py-1 hover:border-input-border hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <TurnIcon aria-hidden className={`h-4 w-4 shrink-0 ${running ? 'animate-spin motion-reduce:animate-none' : ''}`} />
        <span role="status" className="truncate">{title}</span>
        <ChevronRight aria-hidden className={`h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <ol aria-label={t('directorActivity.details')} className="my-2 space-y-2 pl-2">
          {activities.map(activity => {
            const Icon = activity.status === 'running' ? LoaderCircle : activity.status === 'failed' ? X : activity.status === 'cancelled' ? Circle : Check
            const hasDetails = Boolean(activity.text || activity.tool_name || activity.tool_input || activity.tool_result)
            const fields: { key: 'message' | 'input' | 'result'; value: string | null | undefined }[] = [
              { key: 'message', value: activity.text },
              { key: 'input', value: activity.tool_input },
              { key: 'result', value: activity.tool_result },
            ]
            const heading = <><Icon aria-hidden className={`mt-0.5 h-3 w-3 shrink-0 ${activity.status === 'running' ? 'animate-spin motion-reduce:animate-none' : ''}`} /><span>{label(activity)} <span className="text-text-muted">· {t(`directorActivity.activityStates.${activity.status}`)}</span></span></>
            return (
              <li key={activity.id} className="min-w-0">
                {hasDetails ? (
                  <details className="group/activity" onToggle={event => activityLog({ event: 'detail_toggle', turnId: presentation.turn_id, activityId: activity.id, expanded: event.currentTarget.open })}>
                    <summary className="flex cursor-pointer items-start gap-2 rounded-lg border border-input-border bg-surface-1 px-2 py-1 hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                      {heading}
                      <ChevronRight aria-hidden className="ml-auto h-3 w-3 shrink-0 transition-transform group-open/activity:rotate-90 motion-reduce:transition-none" />
                    </summary>
                    <div className="mt-2 space-y-2 rounded-lg border border-border bg-surface-0 p-2">
                      {activity.tool_name && <code className="break-all text-xs font-medium text-foreground">{activity.tool_name}</code>}
                      {fields.map(({ key, value }) => value && (
                        <div key={key}>
                          <div className="mb-1 text-xs font-medium text-text-muted">{t(`directorActivity.raw.${key}`)}</div>
                          <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-xs font-medium text-foreground">{value}</pre>
                        </div>
                      ))}
                    </div>
                  </details>
                ) : <div className="flex items-start gap-2">{heading}</div>}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
